import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Microphone, MapPin, Users, SpeakerHigh, Circle, Radio } from "@phosphor-icons/react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from "react-leaflet";

// Fix the "missing marker icon" bug that ships with Leaflet + webpack.
// eslint-disable-next-line no-underscore-dangle
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Format a Date/ISO string in Malaysia time (UTC+8) regardless of the viewer's
// local machine timezone — server timestamps are in UTC.
const MYT_OPTS = { timeZone: "Asia/Kuala_Lumpur" };
const myTime = (d) => new Date(d).toLocaleTimeString("en-GB", { ...MYT_OPTS, hour12: false });
const myDateTime = (d) => new Date(d).toLocaleString("en-GB", { ...MYT_OPTS, hour12: false });

function wsUrl(path) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const proto = base.startsWith("https") ? "wss" : "ws";
  return base.replace(/^https?/, proto) + path;
}

function tokenFromCookie() {
  // Prefer localStorage (this app stores tokens there); fall back to cookie for legacy.
  try {
    const t = localStorage.getItem("access_token");
    if (t) return t;
  } catch (e) {}
  const m = document.cookie.match(/(?:^|;\s*)access_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView([points[0].lat, points[0].lng], 15); return; }
    const b = L.latLngBounds(points.map((p) => [p.lat, p.lng])).pad(0.2);
    map.fitBounds(b);
  }, [map, points]);
  return null;
}

export default function LiveTab() {
  const [active, setActive] = useState([]);
  const [onChannel, setOnChannel] = useState([]);
  const [audioEvents, setAudioEvents] = useState([]);
  const [talking, setTalking] = useState(false);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const audioQueueRef = useRef([]);
  const playingRef = useRef(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  // --- Poll active workers list every 5s (locations refresh) ---
  const loadActive = async () => {
    try {
      const { data } = await api.get("/punch/live/active");
      setActive(data.active || []);
      setOnChannel(data.on_channel || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    loadActive();
    const t = setInterval(loadActive, 5000);
    return () => clearInterval(t);
  }, []);

  // --- WebSocket connection ---
  useEffect(() => {
    const token = tokenFromCookie();
    if (!token) { toast.error("No auth token — refresh and log in again."); return; }
    const url = wsUrl("/api/punch/live/ws") + `?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      toast.error("Live channel unavailable"); return;
    }
    wsRef.current = ws;
    ws.onopen = () => { setConnected(true); };
    ws.onclose = () => { setConnected(false); };
    ws.onerror = () => { setConnected(false); };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "presence") setOnChannel(msg.listeners || []);
        else if (msg.type === "location") {
          setActive((prev) => prev.map((w) => w.id === msg.from ? { ...w, location: { lat: msg.lat, lng: msg.lng, accuracy: msg.accuracy, updated_at: msg.ts } } : w));
        } else if (msg.type === "audio" && msg.data) {
          setAudioEvents((prev) => [{ from: msg.from, name: msg.from_name, ts: msg.ts, role: msg.from_role }, ...prev].slice(0, 20));
          queueAudio(msg.data, msg.mime || "audio/webm");
        }
      } catch (e) { /* ignore */ }
    };
    return () => { try { ws.close(); } catch (e) {} };
  }, []);

  // --- Playback queue (never overlaps) ---
  const queueAudio = (b64, mime) => {
    try {
      const bin = atob(b64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const blob = new Blob([u8], { type: mime });
      audioQueueRef.current.push(URL.createObjectURL(blob));
      drainQueue();
    } catch (e) { /* ignore */ }
  };
  const drainQueue = () => {
    if (playingRef.current) return;
    const src = audioQueueRef.current.shift();
    if (!src) return;
    playingRef.current = true;
    const a = new Audio(src);
    a.onended = () => { playingRef.current = false; URL.revokeObjectURL(src); drainQueue(); };
    a.onerror = () => { playingRef.current = false; drainQueue(); };
    a.play().catch(() => { playingRef.current = false; drainQueue(); });
  };

  // --- Admin PTT (tap-and-hold) ---
  const startTalk = async () => {
    if (talking || !wsRef.current || wsRef.current.readyState !== 1) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus"
                 : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
                 : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      const opts = mime ? { mimeType: mime } : {};
      const rec = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        blob.arrayBuffer().then((buf) => {
          const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
          wsRef.current?.send(JSON.stringify({ type: "audio", mime: rec.mimeType || "audio/webm", data: b64 }));
        });
      };
      mediaRef.current = rec;
      rec.start();
      setTalking(true);
    } catch (e) { toast.error("Mic permission required to talk"); }
  };
  const stopTalk = () => {
    if (!talking) return;
    try { mediaRef.current?.stop(); } catch (e) {}
    setTalking(false);
  };

  const points = active.filter((w) => w.location).map((w) => ({ id: w.id, name: w.name, lat: w.location.lat, lng: w.location.lng, acc: w.location.accuracy, updated: w.location.updated_at }));
  const centre = points.length ? [points[0].lat, points[0].lng] : [3.139, 101.6869]; // KL default

  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="live-tab">
      <div className="md:col-span-2 bg-white border border-zinc-200 overflow-hidden">
        <div className="bg-zinc-900 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin size={16} weight="fill" className="text-[#FF4F00]" />
            <div className="font-display font-black uppercase text-sm">Live Map</div>
          </div>
          <div className="text-[10px] font-mono opacity-80">{active.length} active · {points.length} located</div>
        </div>
        <div className="h-[520px]" data-testid="live-map">
          <MapContainer center={centre} zoom={13} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={points} />
            {points.map((p) => (
              <>
                <CircleMarker key={`c-${p.id}`} center={[p.lat, p.lng]} radius={Math.min(30, Math.max(6, (p.acc || 20) / 3))} pathOptions={{ color: "#FF4F00", fillOpacity: 0.15 }} />
                <Marker key={p.id} position={[p.lat, p.lng]} eventHandlers={{ click: () => setSelectedWorker(p.id) }}>
                  <Popup>
                    <div className="font-display font-black text-sm">{p.name}</div>
                    <div className="text-[10px] font-mono">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</div>
                    <div className="text-[10px] text-zinc-500">±{Math.round(p.acc || 0)}m · {myTime(p.updated)}</div>
                  </Popup>
                </Marker>
              </>
            ))}
          </MapContainer>
        </div>
      </div>

      <div className="space-y-4">
        {/* Channel + PTT */}
        <div className="bg-white border border-zinc-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Radio size={16} weight="fill" className={connected ? "text-emerald-500" : "text-zinc-400"} />
            <div className="font-display font-black uppercase text-sm">Walkie-Talkie</div>
            <span className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 border ${connected ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-zinc-300 text-zinc-500"}`} data-testid="ws-status">{connected ? "LIVE" : "OFFLINE"}</span>
          </div>
          <button
            onPointerDown={startTalk}
            onPointerUp={stopTalk}
            onPointerCancel={stopTalk}
            onPointerLeave={stopTalk}
            disabled={!connected}
            data-testid="admin-ptt-btn"
            className={`w-full h-24 flex flex-col items-center justify-center gap-1 select-none transition-all ${talking ? "bg-red-600 text-white ring-4 ring-red-300 scale-[0.98]" : "bg-zinc-900 hover:bg-zinc-800 text-white"} disabled:opacity-40`}
          >
            <Microphone size={28} weight={talking ? "fill" : "regular"} />
            <span className="font-display font-black text-xs uppercase">{talking ? "TALKING…" : "Hold to talk"}</span>
          </button>
          <div className="text-[10px] text-zinc-500 mt-2 font-mono">
            Everyone on the channel hears you. Release the button to send.
          </div>
        </div>

        {/* On-channel listeners */}
        <div className="bg-white border border-zinc-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} />
            <div className="font-display font-black uppercase text-sm">On Channel ({onChannel.length})</div>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {onChannel.length === 0 && <div className="text-xs text-zinc-400">Nobody is connected right now.</div>}
            {onChannel.map((u, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" data-testid={`channel-user-${i}`}>
                <Circle size={8} weight="fill" className="text-emerald-500" />
                <span>{u.name}</span>
                <span className="text-[9px] font-mono text-zinc-400 ml-auto">{u.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Active workers list */}
        <div className="bg-white border border-zinc-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} />
            <div className="font-display font-black uppercase text-sm">Active Workers ({active.length})</div>
          </div>
          <div className="space-y-2 max-h-52 overflow-y-auto">
            {active.length === 0 && <div className="text-xs text-zinc-400">No workers are punched in.</div>}
            {active.map((w) => (
              <div key={w.id} className={`border px-2 py-1.5 text-sm ${selectedWorker === w.id ? "border-[#002FA7] bg-blue-50" : "border-zinc-200"}`} data-testid={`active-worker-${w.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{w.name}</span>
                  {w.location
                    ? <span className="text-[10px] text-emerald-700 font-mono">📍 located</span>
                    : <span className="text-[10px] text-amber-700 font-mono">no location</span>}
                </div>
                {w.location && (
                  <div className="text-[10px] font-mono text-zinc-500">
                    {w.location.lat.toFixed(5)}, {w.location.lng.toFixed(5)} · ±{Math.round(w.location.accuracy || 0)}m · {myTime(w.location.updated_at)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recent audio log */}
        <div className="bg-white border border-zinc-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <SpeakerHigh size={16} />
            <div className="font-display font-black uppercase text-sm">Live Audio Feed</div>
          </div>
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {audioEvents.length === 0 && <div className="text-xs text-zinc-400">No audio yet.</div>}
            {audioEvents.map((a, i) => (
              <div key={i} className="text-xs flex items-center gap-2">
                <SpeakerHigh size={10} className="text-[#002FA7]" />
                <span className="font-semibold">{a.name}</span>
                <span className="text-[9px] font-mono text-zinc-400 ml-auto">{myTime(a.ts)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Audio History (kept for 30 days) */}
        <AudioHistoryPanel workers={active} />
      </div>
    </div>
  );
}

function AudioHistoryPanel({ workers }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [workerId, setWorkerId] = useState("");
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (workerId) params.worker_id = workerId;
      const { data } = await api.get("/punch/live/audio-history", { params });
      setClips(data.clips || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const play = async (clip) => {
    try {
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setPlayingId(clip.id);
      const { data } = await api.get(`/punch/live/audio/${clip.id}`);
      const bin = atob(data.data);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([u8], { type: data.mime || "audio/webm" }));
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => { setPlayingId(null); URL.revokeObjectURL(url); };
      a.onerror = () => { setPlayingId(null); URL.revokeObjectURL(url); toast.error("Couldn't play clip"); };
      await a.play();
    } catch (e) { setPlayingId(null); toast.error(formatApiError(e)); }
  };

  const stop = () => {
    try { audioRef.current?.pause(); } catch (e) {}
    setPlayingId(null);
  };

  const fmtSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="bg-white border border-zinc-200 p-4" data-testid="audio-history-panel">
      <div className="flex items-center gap-2 mb-3">
        <SpeakerHigh size={16} />
        <div className="font-display font-black uppercase text-sm">Audio History</div>
        <span className="ml-auto text-[9px] font-mono text-zinc-500">30-day archive</span>
      </div>
      <div className="grid grid-cols-2 gap-1 mb-2">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="border border-zinc-200 px-2 py-1 text-xs font-mono" data-testid="audio-history-from" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="border border-zinc-200 px-2 py-1 text-xs font-mono" data-testid="audio-history-to" />
      </div>
      <div className="flex gap-1 mb-2">
        <select value={workerId} onChange={(e) => setWorkerId(e.target.value)} className="flex-1 border border-zinc-200 px-2 py-1 text-xs" data-testid="audio-history-worker">
          <option value="">All speakers</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <button onClick={load} className="bg-[#002FA7] text-white text-xs px-3 py-1" data-testid="audio-history-load">{loading ? "…" : "Load"}</button>
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {clips.length === 0 && !loading && <div className="text-xs text-zinc-400 py-2 text-center">No clips in this range.</div>}
        {clips.map((c) => (
          <div key={c.id} className="flex items-center gap-2 border border-zinc-100 px-2 py-1.5 text-xs" data-testid={`audio-clip-${c.id}`}>
            <button
              onClick={() => (playingId === c.id ? stop() : play(c))}
              className={`w-7 h-7 flex items-center justify-center transition-colors ${playingId === c.id ? "bg-red-600 text-white" : "bg-[#002FA7] text-white hover:bg-[#00227A]"}`}
              data-testid={`audio-play-${c.id}`}
              title={playingId === c.id ? "Stop" : "Play"}
            >
              {playingId === c.id ? "◼" : "▶"}
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{c.from_name}</div>
              <div className="text-[10px] font-mono text-zinc-500">
                {myDateTime(c.ts)} · {fmtSize(c.size_bytes)}
              </div>
            </div>
            <span className={`text-[9px] font-mono px-1.5 py-0.5 border ${c.from_role === "punch_worker" ? "border-emerald-300 text-emerald-700" : "border-blue-300 text-blue-700"}`}>{c.from_role.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
